import React from 'react';

const About = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section
        className="relative h-[400px] bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: 'url(/images/hero_images/guava_farm.png)' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Story</h1>
          <p className="text-xl md:text-2xl">Discover Primitive Tropics That Form</p>
        </div>
      </section>

      {/* Our History Section */}
      <section className="py-16 px-4 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Our History</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Siri Farms began its journey in 2020 and has grown into a thriving agricultural venture in Orlando, Florida. We specialize in cultivating premium quality tropical fruits, including dragonfruits, mangoes, longans, and much more.
            </p>
            <p className="text-gray-600 leading-relaxed">
              What started as a small family initiative has blossomed into a commitment to providing fresh, locally sourced produce to our community. Our commitment is to bring the freshest, most flavorful tropical produce from our fields to your doorstep across the USA.
            </p>
          </div>
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img
              src="/images/hero_images/farm_pane.png"
              alt="Our Farm History"
              className="w-full h-[350px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-16 px-4 md:px-16 lg:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 rounded-lg overflow-hidden shadow-lg">
            <img
              src="/images/hero_images/greenhouse.png"
              alt="Our Mission"
              className="w-full h-[350px] object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Our mission is to create a sustainable food system that benefits our community by promoting small-scale farming and healthy sustainable communities. We continue to serve customers across the USA with the finest tropical produce.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We believe in the power of local agriculture to transform communities and provide families with access to fresh, nutritious produce.
            </p>
          </div>
        </div>
      </section>

      {/* Our Core Values Section */}
      <section className="py-16 px-4 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-12">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Sustainability */}
            <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 bg-organic-green rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Sustainability</h3>
              <p className="text-gray-600 text-sm">We practice sustainable farming methods to protect our environment for future generations.</p>
            </div>

            {/* Local Produce */}
            <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 bg-organic-green rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Local Produce</h3>
              <p className="text-gray-600 text-sm">Freshly grown produce straight from our farm to your table with minimal travel time.</p>
            </div>

            {/* Community First */}
            <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 bg-organic-green rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Community First</h3>
              <p className="text-gray-600 text-sm">Supporting local families and building lasting relationships within our community.</p>
            </div>

            {/* Biodiversity */}
            <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 bg-organic-green rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Biodiversity</h3>
              <p className="text-gray-600 text-sm">Celebrating the variety of tropical fruits to offer a diverse selection for our customers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Farming Practices Section */}
      <section className="py-16 px-4 md:px-16 lg:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-12">Our Farming Practices</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Water Conservation */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Water Conservation</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We use drip irrigation and rainwater harvesting to minimize water waste while ensuring our crops receive optimal hydration. Our sustainable water management practices help preserve this precious resource.
              </p>
            </div>

            {/* Natural Pest Control */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 mb-4 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Natural Pest Control</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We rely on beneficial insects and organic methods to control pests without harmful chemicals. This approach protects both our produce and the environment while delivering healthier fruits.
              </p>
            </div>

            {/* Composting */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 mb-4 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Composting</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                All our organic waste is composted and returned to the soil. This natural cycle enriches our farmland, promotes healthy plant growth, and reduces our environmental footprint.
              </p>
            </div>
          </div>

          {/* Farming Practices Image */}
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img
              src="/images/hero_images/leaves.png"
              alt="Our Farming Practices"
              className="w-full h-[400px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Farm Facts Section */}
      <section className="py-16 px-4 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-12">Farm Facts</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Acres */}
            <div className="text-center p-8 border border-gray-200 rounded-lg">
              <p className="text-5xl font-bold text-organic-green mb-2">400+</p>
              <p className="text-gray-600">Acres of Tropical Orchards</p>
            </div>

            {/* Years */}
            <div className="text-center p-8 border border-gray-200 rounded-lg">
              <p className="text-5xl font-bold text-organic-green mb-2">7+</p>
              <p className="text-gray-600">Years of Excellence in Farming</p>
            </div>

            {/* Happy Customers */}
            <div className="text-center p-8 border border-gray-200 rounded-lg">
              <p className="text-5xl font-bold text-organic-green mb-2">1000+</p>
              <p className="text-gray-600">Happy Customers Served</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;